import { createSelector } from 'reselect';
import { downloader, postman } from '../utils/postman';
import { all, put, takeEvery } from 'redux-saga/effects';
import { toast } from 'react-toastify';
import {errorMapping} from "../utils/errorMapping";

//*  TYPES  *//

const UPLOAD_CONFIG_REQUEST = 'UPLOAD_CONFIG_REQUEST';
const UPLOAD_CONFIG_SUCCESS = 'UPLOAD_CONFIG_SUCCESS';

const UPLOAD_FILE_REQUEST = 'UPLOAD_FILE_REQUEST';
const UPLOAD_FILE_SUCCESS = 'UPLOAD_FILE_SUCCESS';
const UPLOAD_FILE_ERROR = 'UPLOAD_FILE_ERROR';

const GET_DOCUMENTS_REQUEST = 'GET_DOCUMENTS_REQUEST';
const GET_DOCUMENTS_SUCCESS = 'GET_DOCUMENTS_SUCCESS';
const GET_DOCUMENTS_ERROR = 'GET_DOCUMENTS_ERROR';

const ADD_DOCUMENT_REQUEST = 'ADD_DOCUMENT_REQUEST';
const ADD_DOCUMENT_SUCCESS = 'ADD_DOCUMENT_SUCCESS';
const ADD_DOCUMENT_ERROR = 'ADD_DOCUMENT_ERROR';

const EDIT_DOCUMENT_REQUEST = 'EDIT_DOCUMENT_REQUEST';
const EDIT_DOCUMENT_SUCCESS = 'EDIT_DOCUMENT_SUCCESS';
const EDIT_DOCUMENT_ERROR = 'EDIT_DOCUMENT_ERROR';

const DELETE_DOCUMENT_REQUEST = 'DELETE_DOCUMENT_REQUEST';
const DELETE_DOCUMENT_SUCCESS = 'DELETE_DOCUMENT_SUCCESS';
const DELETE_DOCUMENT_ERROR = 'DELETE_DOCUMENT_ERROR';

const DOWNLOAD_DOCUMENT_REQUEST = 'DOWNLOAD_DOCUMENT_REQUEST';
const DOWNLOAD_DOCUMENT_SUCCESS = 'DOWNLOAD_DOCUMENT_SUCCESS';
const DOWNLOAD_DOCUMENT_ERROR = 'DOWNLOAD_DOCUMENT_ERROR';

const ADD_SHIPPINGS_DOCUMENT_REQUEST = 'ADD_SHIPPINGS_DOCUMENT_REQUEST';
const ADD_SHIPPINGS_DOCUMENT_SUCCESS = 'ADD_SHIPPINGS_DOCUMENT_SUCCESS';
const ADD_SHIPPINGS_DOCUMENT_ERROR = 'ADD_SHIPPINGS_DOCUMENT_ERROR';

const CLEAR_DOCUMENTS = 'CLEAR_DOCUMENTS';
const CLEAR_ERROR = 'CLEAR_ERROR';

const DEFAULT_STATE = 'DEFAULT_STATE';

//*  INITIAL STATE  *//

const initial = {
    progress: false,
    uploadProgress: false,
    addProgress: false,
    config: {},
    documentTypes: [],
    documents: [],
    error: []
};

//*  REDUCER  *//

export default (state = initial, { type, payload }) => {
    switch (type) {
        case UPLOAD_CONFIG_SUCCESS:
            return {
                ...state,
                config: payload,
            };
        case GET_DOCUMENTS_REQUEST:
            return {
                ...state,
                progress: true,
            };
        case GET_DOCUMENTS_SUCCESS:
            return {
                ...state,
                documents: payload,
                progress: false,
            };
        case GET_DOCUMENTS_ERROR:
            return {
                ...state,
                progress: false,
                documents: [],
            };
        case UPLOAD_FILE_REQUEST:
            return {
                ...state,
                progress: true,
                uploadProgress: true,
            };
        case UPLOAD_FILE_ERROR:
        case UPLOAD_FILE_SUCCESS:
            return {
                ...state,
                progress: false,
                uploadProgress: false,
            };
        case ADD_DOCUMENT_REQUEST:
            return {
                ...state,
                progress: true,
                addProgress: true,
            };
        case ADD_DOCUMENT_SUCCESS:
        case ADD_DOCUMENT_ERROR:
            return {
                ...state,
                progress: false,
                addProgress: false,
                error: payload.errors
            };
        case ADD_SHIPPINGS_DOCUMENT_REQUEST:
            return {
                ...state,
                progress: true,
                addProgress: true,
            };
        case ADD_SHIPPINGS_DOCUMENT_SUCCESS:
        case ADD_SHIPPINGS_DOCUMENT_ERROR:
            return {
                ...state,
                progress: false,
                addProgress: false,
                error: payload.errors
            };
        case CLEAR_DOCUMENTS:
            return {
                ...state,
                documents: [],
            };
        case EDIT_DOCUMENT_REQUEST:
            return {
                ...state,
                progress: true
            };
        case EDIT_DOCUMENT_SUCCESS:
        case EDIT_DOCUMENT_ERROR:
            return {
                ...state,
                progress: false,
                error: payload.errors
            };
        case CLEAR_ERROR:
            return {
                ...state,
                error: state.error && state.error.filter(item => item.name !== payload),
            };
        case DEFAULT_STATE:
            return {
                ...initial
            };
        default:
            return state;
    }
};

//*  ACTION CREATORS  *//

export const uploadConfigRequest = payload => {
    return {
        type: UPLOAD_CONFIG_REQUEST,
        payload,
    };
};

export const uploadFileRequest = payload => {
    return {
        type: UPLOAD_FILE_REQUEST,
        payload,
    };
};

export const addDocumentRequest = payload => {
    return {
        type: ADD_DOCUMENT_REQUEST,
        payload,
    };
};

export const getDocumentsRequest = payload => {
    return {
        type: GET_DOCUMENTS_REQUEST,
        payload,
    };
};

export const editDocumentRequest = payload => {
    return {
        type: EDIT_DOCUMENT_REQUEST,
        payload,
    };
};

export const deleteDocumentRequest = payload => {
    return {
        type: DELETE_DOCUMENT_REQUEST,
        payload,
    };
};

export const downloadDocumentRequest = payload => {
    return {
        type: DOWNLOAD_DOCUMENT_REQUEST,
        payload,
    };
};

export const clearDocuments = () => {
    return {
        type: CLEAR_DOCUMENTS,
    };
};

export const clearError = payload => {
    return {
        type: CLEAR_ERROR,
        payload,
    };
};

export const addShippingsDocumentRequest = payload => {
    return {
        type: ADD_SHIPPINGS_DOCUMENT_REQUEST,
        payload,
    };
};

//*  SELECTORS *//

const stateSelector = state => state.documents;

export const uploadProgressSelector = createSelector(stateSelector, state => state.uploadProgress);

export const documentTypesSelector = createSelector(
    stateSelector,
    state =>
        state.documentTypes &&
        state.documentTypes.map(x => ({
            key: x.id,
            value: x.id,
            text: x.name,
        })),
);

export const documentsSelector = createSelector(stateSelector, state => state.documents);
export const progressSelector = createSelector(stateSelector, state => state.progress);
export const errorSelector = createSelector(stateSelector, state => errorMapping(state.error));
export const configSelector = createSelector(stateSelector, state => state.config);

//*  SAGA  *//

function* uploadConfigSaga({ payload }) {
    try {
        const result = yield postman.get(`/files/config`);
        yield put({
            type: UPLOAD_CONFIG_SUCCESS,
            payload: result,
        });
    } catch (e) {
        console.log(e);
    }
}

function* uploadFileSaga({ payload }) {
    try {
        const { form, fileName, callbackSuccess, isBase64 } = payload;
        const result = yield postman.post(`/files/${isBase64 ? 'base64' : 'upload'}`, form, {
            headers: {
                'Content-Type': isBase64 ? 'application/json-patch+json' : 'multipart/form-data',
            },
        });
        if (!result.isError) {
            yield put({
                type: UPLOAD_FILE_SUCCESS,
                payload: {
                    id: result.id,
                    fileName,
                },
            });
        } else {
            result.message && toast.error(result.message, {
                autoClose: false,
            });
            yield put({
                type: UPLOAD_FILE_ERROR,
                payload: result,
            });
        }
        

        callbackSuccess(result.id);
    } catch (e) {
        toast.error(e.message, {
            autoClose: false,
        });
        yield put({
            type: UPLOAD_FILE_ERROR,
            payload: e,
        });
    }
}

function* addDocumentSaga({ payload }) {
    try {
        const { gridName, cardId, document, callbackSuccess } = payload;
        const result = yield postman.post(`/${gridName}/${cardId}/documents`, document);

        yield put({
            type: ADD_DOCUMENT_SUCCESS,
            payload: result
        });

        (result && !result.isError) && callbackSuccess();
    } catch (e) {
        yield put({
            type: ADD_DOCUMENT_ERROR,
            payload: e,
        });
    }
}

function* addShippingsDocumentSaga({ payload }) {
    try {
        const { ids, document, callbackSuccess } = payload;
        const result = yield postman.post(`/shippings/addDocument`, { ids, document });

        yield put({
            type: ADD_SHIPPINGS_DOCUMENT_SUCCESS,
            payload: result
        });

        (result && !result.isError) && callbackSuccess();
    } catch (e) {
        yield put({
            type: ADD_SHIPPINGS_DOCUMENT_ERROR,
            payload: e,
        });
    }
}

function* getDocumentsSaga({ payload }) {
    try {
        const { gridName, cardId } = payload;
        const result = yield postman.get(`/${gridName}/${cardId}/documents`);

        yield put({
            type: GET_DOCUMENTS_SUCCESS,
            payload: result,
        });
    } catch (e) {
        yield put({
            type: GET_DOCUMENTS_ERROR,
            payload: e,
        });
    }
}

function* editDocumentSaga({ payload }) {
    try {
        const { gridName, cardId, id, document, callbackSuccess } = payload;
        const result = yield postman.put(`/${gridName}/${cardId}/documents/${id}`, document);

        yield put({
            type: EDIT_DOCUMENT_SUCCESS,
            payload: result
        });

        (result && !result.isError && callbackSuccess) && callbackSuccess();
    } catch (e) {
        yield put({
            type: EDIT_DOCUMENT_ERROR,
            payload: e,
        });
    }
}

function* deleteDocumentSaga({ payload }) {
    try {
        const { gridName, cardId, id, callbackSuccess } = payload;
        const result = yield postman.delete(`/${gridName}/${cardId}/documents/${id}`);

        yield put({
            type: DELETE_DOCUMENT_SUCCESS,
        });
        callbackSuccess();
    } catch (e) {
        yield put({
            type: DELETE_DOCUMENT_ERROR,
            payload: e,
        });
    }
}

function* downloadDocumentSaga({ payload }) {
    try {
        const { id } = payload;
        const res = yield downloader.get(`/files/${id}/download`, { responseType: 'blob' });
        const { data } = res;
        let headerLine = res.headers['content-disposition'].split('; ')[2];
        // console.log('headerLine', headerLine.split('; ')[2]);
        /*  let startFileNameIndex = headerLine[2].indexOf("''");
          /!*let endFileNameIndex = headerLine[2].lastIndexOf('"');*!/*/
        let filename = decodeURI(headerLine.substring(17));
        const link = document.createElement('a');
        link.href = URL.createObjectURL(new Blob([data], { type: data.type }));
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();

        yield put({
            type: DOWNLOAD_DOCUMENT_SUCCESS,
        });
    } catch (e) {
        if (e && e.response && e.response.status === 404) {
            toast.error('Запрошенный файл не найден');
        }
        yield put({
            type: DOWNLOAD_DOCUMENT_ERROR,
            payload: e,
        });
    }
}

export function* saga() {
    yield all([
        takeEvery(UPLOAD_CONFIG_REQUEST, uploadConfigSaga),
        takeEvery(UPLOAD_FILE_REQUEST, uploadFileSaga),
        takeEvery(ADD_DOCUMENT_REQUEST, addDocumentSaga),
        takeEvery(ADD_SHIPPINGS_DOCUMENT_REQUEST, addShippingsDocumentSaga),
        takeEvery(GET_DOCUMENTS_REQUEST, getDocumentsSaga),
        takeEvery(EDIT_DOCUMENT_REQUEST, editDocumentSaga),
        takeEvery(DELETE_DOCUMENT_REQUEST, deleteDocumentSaga),
        takeEvery(DOWNLOAD_DOCUMENT_REQUEST, downloadDocumentSaga),
    ]);
}
